import { PageType } from '@flex-apis/recruiting';
import { fetchI18nResources, getTranslationsProps } from '@flex-packages/i18n';
import { serverSideTranslations } from '@flex-packages/i18n/next/serverSideTranslations';
import type { PublishedRecruitingSiteResponse } from '@flex-private-packages/recruiting-site-builder/base';
import { generateSiteDesign } from '@flex-private-packages/recruiting-site-builder/base';
import type { GetServerSideProps } from 'next';

import type { WithErrorProps } from '../../src';
import {
  assertPublishedSite,
  fetchRecruitingSite,
  getExternalRedirectResult,
  getSubdomainFromServerSidePropsContext,
  handleServerSideError,
  handleSystemSubdomain,
  JobDescriptionListPage,
  useSendAccessLog,
  withErrorPage,
} from '../../src';
import { WithLanguageDetection } from '../../src/app/commons/WithLanguageDetection';
import { getServerAxios } from '../../src/utils/axios';

interface Props {
  recruitingSiteResponse: PublishedRecruitingSiteResponse;
}

function JobDescriptions({ recruitingSiteResponse }: Props) {
  const siteDesign = generateSiteDesign(recruitingSiteResponse);

  useSendAccessLog({
    customerIdHash: siteDesign.customerIdHash,
    pageType: PageType.JobDescription,
  });

  return (
    <WithLanguageDetection>
      <JobDescriptionListPage siteDesign={siteDesign} />
    </WithLanguageDetection>
  );
}

const JobDescriptionsWithErrorPage = withErrorPage(JobDescriptions);
export default JobDescriptionsWithErrorPage;

type ServerSideProps = WithErrorProps<Props>;
export const getServerSideProps: GetServerSideProps<
  ServerSideProps
> = async context => {
  try {
    const axiosInstance = getServerAxios({
      req: context.req,
      res: context.res,
    });
    const subdomain = getSubdomainFromServerSidePropsContext(context);
    await handleSystemSubdomain({ context, axiosInstance, subdomain });

    const [recruitingSiteResponse, { props: translationProps }] =
      await Promise.all([
        fetchRecruitingSite(axiosInstance, { subdomain }),
        getTranslationsProps(
          serverSideTranslations,
          fetchI18nResources(require.context('../../locales'))
        )(context),
      ]);

    const externalRedirect = getExternalRedirectResult(recruitingSiteResponse);
    if (externalRedirect !== null) {
      return externalRedirect;
    }

    assertPublishedSite(recruitingSiteResponse);

    return {
      props: {
        recruitingSiteResponse,
        ...translationProps,
      },
    };
  } catch (error) {
    return handleServerSideError(context, error);
  }
};
